import { Injectable } from '@angular/core';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export const MINUTES_IN = {
  minutes: 1,
  hours: 60,
  days: 1440,
  weeks: 10080,
  months: 43200, // 30 days
} as const;

export type TimeUnit = keyof typeof MINUTES_IN; // 'minutes' | 'hours' | 'days' | 'weeks' | 'months'
export type AppDuration = { amount: number; unit: TimeUnit };

@Injectable({
  providedIn: 'root',
})
export class DateTimeUtils {
  public dayFormat = 'YYYY-MM-DD';
  public timeFormatHM = 'HH:mm';
  public dayTimeFormat = 'YYYY-MM-DD HH:mm:ss';

  getTodayFromTimezone(timezone: string): Date {
    // get current day from the given timezone
    const todayFromTimezone = dayjs().tz(timezone).startOf('day');
    return todayFromTimezone.toDate();
  }

  switchTimezone(date: Date | string, timeZone: string): Date {
    return dayjs.tz(this.formatDayTime(date), timeZone).toDate();
  }

  setTime(date: Date, timeString: string, timeZone: string): Date {
    const dayString = this.formatDay(date);
    return dayjs.tz(`${dayString} ${timeString}`, timeZone).toDate();
  }

  addMonths(date: Date, months: number): Date {
    return dayjs(date).add(months, 'month').toDate();
  }

  addDurationToTime(startTime: string, duration: number): string {
    if (!startTime) return startTime;

    const startHour = parseInt(startTime.split(':')[0]);
    const startMinute = parseInt(startTime.split(':')[1]);
    const durationHours = Math.floor(duration / 60);
    const durationMinutes = duration % 60;
    let endHour = startHour + durationHours;
    let endMinute = startMinute + durationMinutes;
    if (endMinute >= 60) {
      endHour += 1;
      endMinute -= 60;
    }
    return `${endHour.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}`;
  }

  getEarliestDate(date: Date, timeSlots: string[]): Date {
    if (!timeSlots || timeSlots.length === 0) {
      return date;
    }

    const day = date.toISOString().split('T')[0];
    timeSlots.sort();
    const earliestTime = timeSlots[0].substring(0, 5);

    // Build a proper ISO string in UTC.
    const isoString = `${day}T${earliestTime}:00.000Z`;
    return new Date(isoString);
  }

  getOccupiedTimeSlots(startTime: string, interval: number, serviceDuration: number): string[] {
    console.log('>>> getOccupiedTimeSlots', startTime, interval, serviceDuration);
    const occupiedSlots: string[] = [];
    const endDate = new Date(`2000-01-01 ${this.addDurationToTime(startTime, serviceDuration)}`);
    let crtTime = startTime;

    console.log('>>> endDate', endDate);
    console.log('>>> crtTime', new Date(`2000-01-01 ${crtTime}`));
    do {
      occupiedSlots.push(crtTime);
      crtTime = this.addDurationToTime(crtTime, interval);
    } while (new Date(`2000-01-01 ${crtTime}`) < endDate);

    console.log('>>> occupiedSlots', occupiedSlots);
    return occupiedSlots;
  }

  isSlotsOverlap(currentSlot: string, currentDuration: number, targetSlot: string): boolean {
    const [currentSlotHours, currentSlotMinutes] = currentSlot.split(':').map(Number);
    const currentSlotStartTime = dayjs()
      .set('hours', currentSlotHours)
      .set('minutes', currentSlotMinutes)
      .set('seconds', 0);
    const currentSlotEndTime = currentSlotStartTime.add(currentDuration, 'minutes');

    const [targetSlotHours, targetSlotMinutes] = targetSlot.split(':').map(Number);
    const targetSlotStartTime = dayjs()
      .set('hours', targetSlotHours)
      .set('minutes', targetSlotMinutes)
      .set('seconds', 0);

    return targetSlotStartTime.isAfter(currentSlotStartTime) && targetSlotStartTime.isBefore(currentSlotEndTime);
  }

  formatDay(date: Date | string, timeZone?: string): string {
    if (timeZone) {
      return dayjs(date).tz(timeZone).format(this.dayFormat);
    }
    return dayjs(date).format(this.dayFormat);
  }

  formatTime(date: Date | string, timeZone?: string): string {
    if (timeZone) {
      return dayjs(date).tz(timeZone).format(this.timeFormatHM);
    }
    return dayjs(date).format(this.timeFormatHM);
  }

  formatDayTime(date: Date | string): string {
    return dayjs(date).format(this.dayTimeFormat);
  }

  getWeekStartForCountry(countryCode: string): number {
    const localeLabel = `en-${countryCode}`;
    try {
      const locale = new Intl.Locale(localeLabel) as any;
      // Safari uses getWeekInfo(), other browsers expose weekInfo
      const weekInfo = typeof locale.getWeekInfo === 'function' ? locale.getWeekInfo() : locale.weekInfo;
      // Returns 0 for Sunday or 1 for Monday
      if (weekInfo?.firstDay != null) {
        return weekInfo.firstDay % 7;
      }

      // Fallback if week info not available
      return 0;
    } catch (error) {
      console.error('Intl.Locale not supported or invalid country code', localeLabel, error);
      return 0; // Default to Sunday
    }
  }

  convertMinutesToDuration(total: number): AppDuration {
    if (total >= MINUTES_IN.months) {
      return { amount: Math.round(total / MINUTES_IN.months), unit: 'months' };
    }
    if (total % MINUTES_IN.weeks === 0 && total / MINUTES_IN.weeks <= 52) {
      return { amount: total / MINUTES_IN.weeks, unit: 'weeks' };
    }
    if (total % MINUTES_IN.days === 0 && total / MINUTES_IN.days <= 365) {
      return { amount: total / MINUTES_IN.days, unit: 'days' };
    }
    if (total % MINUTES_IN.hours === 0 && total / MINUTES_IN.hours <= 720) {
      return { amount: total / MINUTES_IN.hours, unit: 'hours' };
    }
    return { amount: total, unit: 'minutes' };
  }
}
